import {
  useCallback, useEffect, useRef, useState,
  type PointerEvent,
} from 'react';
import type { Tone } from './state.ts';
import { axisClock, ticks } from './timeScale.ts';
import styles from './Plot.module.css';

/* ==========================================================================
   Plot — a multi-series line plot, hand-drawn as SVG.

   Everything THE BAR §8.6 calls a library default is absent: no gradient
   fill, no rounded caps, no drop shadow, no legend as a centred row of pills,
   no animated entry, no axis labels at 14px. What is present is the thing
   that makes a plot an instrument rather than a picture — a crosshair with a
   two-column readout listing every series' value at that instant.

   The grid is 1px --plot-grid on the panel ground. Series colour comes from
   the semantic palette (via `tone`) or from the fixed --viz-* ramp, in order.
   ========================================================================== */

export interface PlotSeries {
  key: string;
  label: string;
  /** Aligned index-for-index with `times`. null breaks the line. */
  values: readonly (number | null | undefined)[];
  /** Semantic colour. Falls back to the --viz-* ramp by declaration order. */
  tone?: Tone | undefined;
  unit?: string | undefined;
  digits?: number | undefined;
  /** Dashed 1px rule at a limit the operator judges this series against. */
  threshold?: number | undefined;
}

const RAMP = ['var(--viz-1)', 'var(--viz-2)', 'var(--viz-3)', 'var(--viz-4)',
  'var(--viz-5)', 'var(--viz-6)', 'var(--viz-7)', 'var(--viz-8)'];

const PAD_T = 13;
const PAD_B = 2;
const PAD_L = 0;
const PAD_R = 0;

function niceStep(raw: number): number {
  if (raw <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const n = raw / mag;
  const m = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10;
  return m * mag;
}

function yTicks(lo: number, hi: number, count: number): number[] {
  if (!(hi > lo)) return [lo];
  const step = niceStep((hi - lo) / Math.max(1, count));
  const out: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + step * 0.001; v += step) out.push(v);
  return out;
}

function useWidth(): [(el: HTMLDivElement | null) => void, number] {
  const [w, setW] = useState(0);
  const obs = useRef<ResizeObserver | null>(null);
  useEffect(() => () => obs.current?.disconnect(), []);
  const ref = useCallback((el: HTMLDivElement | null) => {
    obs.current?.disconnect();
    if (!el) return;
    setW(el.getBoundingClientRect().width);
    const o = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setW(r.width);
    });
    o.observe(el);
    obs.current = o;
  }, []);
  return [ref, w];
}

export function Plot({
  times,
  series,
  height = 128,
  min,
  max,
  unit,
  legend = true,
  now,
  cursor: externalCursor,
  readout = true,
}: {
  /** epoch ms, ascending. */
  times: readonly number[];
  series: readonly PlotSeries[];
  height?: number | undefined;
  /** Pin the y-range so the trace does not re-scale itself every poll. */
  min?: number | undefined;
  max?: number | undefined;
  /** Shared unit. Rides the top tick as a suffix; per-series units win. */
  unit?: string | undefined;
  legend?: boolean | undefined;
  now?: number | undefined;
  /**
   * Externally driven crosshair, in epoch ms — pass the cursor from a
   * RibbonStack so the ribbons and the trace share ONE playhead instead of
   * two that disagree about the instant. When set, the plot stops listening
   * for its own pointer.
   */
  cursor?: number | null | undefined;
  /** Off when something upstream already prints the values at the cursor. */
  readout?: boolean | undefined;
}) {
  const [ref, w] = useWidth();
  const [own, setOwn] = useState<number | null>(null);
  const driven = externalCursor !== undefined;

  const from = times[0] ?? 0;
  const to = times[times.length - 1] ?? from + 1;

  const all: number[] = [];
  for (const s of series) {
    for (const v of s.values) if (v != null && !Number.isNaN(v)) all.push(v);
  }
  const rawLo = min ?? (all.length > 0 ? Math.min(...all) : 0);
  const rawHi = max ?? (all.length > 0 ? Math.max(...all) : 1);
  const pad = rawHi === rawLo ? 1 : (rawHi - rawLo) * 0.08;
  const lo = min ?? rawLo - pad;
  const hi = max ?? rawHi + pad;

  const iw = Math.max(1, w - PAD_L - PAD_R);
  const ih = Math.max(1, height - PAD_T - PAD_B);
  const px = (i: number) =>
    PAD_L + (times.length <= 1 ? iw / 2 : (i / (times.length - 1)) * iw);
  const py = (v: number) => PAD_T + ih - ((v - lo) / (hi - lo || 1)) * ih;

  const idxAt = (clientFrac: number) =>
    times.length === 0 ? -1 : Math.round(clientFrac * (times.length - 1));

  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const f = Math.max(0, Math.min(1, (e.clientX - r.left - PAD_L) / Math.max(1, r.width - PAD_L - PAD_R)));
    setOwn(idxAt(f));
  };

  // A driven cursor snaps to the nearest sample so the marker sits on a real
  // reading; the line itself is drawn at the true cursor time, not the sample.
  const cursor = driven
    ? externalCursor == null || to <= from
      ? null
      : idxAt((externalCursor - from) / (to - from))
    : own;
  const cursorX = driven && externalCursor != null
    ? PAD_L + Math.max(0, Math.min(1, (externalCursor - from) / (to - from || 1))) * iw
    : cursor === null ? 0 : px(cursor);

  const path = (s: PlotSeries) => {
    const segs: string[] = [];
    let cur: string[] = [];
    s.values.forEach((v, i) => {
      if (v == null || Number.isNaN(v) || i >= times.length) {
        if (cur.length > 1) segs.push(cur.join(' '));
        cur = [];
        return;
      }
      cur.push(`${cur.length === 0 ? 'M' : 'L'}${px(i).toFixed(2)},${py(v).toFixed(2)}`);
    });
    if (cur.length > 1) segs.push(cur.join(' '));
    return segs;
  };

  const colourOf = (s: PlotSeries, i: number) =>
    s.tone === undefined ? RAMP[i % RAMP.length]! : 'var(--tone)';

  const ys = yTicks(lo, hi, 3);
  const xs = ticks(from, to, Math.max(2, Math.round(w / 110)));

  return (
    <div
      className={styles.plot}
      style={{ height: `${height}px` }}
      ref={ref}
      {...(driven ? {} : { onPointerMove: onMove, onPointerLeave: () => setOwn(null) })}
    >
      <svg
        width={w}
        height={height}
        role="img"
        aria-label={`${series.map((s) => s.label).join(', ')} from ${axisClock(from)} to ${axisClock(to)}`}
      >
        {ys.map((v) => (
          <line
            key={`y${v}`}
            className={styles.grid}
            x1={PAD_L} x2={PAD_L + iw}
            y1={py(v)} y2={py(v)}
          />
        ))}
        {xs.map((t) => {
          const i = (t.f * iw) + PAD_L;
          return (
            <line key={`x${t.t}`} className={styles.grid} x1={i} x2={i} y1={PAD_T} y2={PAD_T + ih} />
          );
        })}

        {series.map((s, i) =>
          s.threshold === undefined || s.threshold < lo || s.threshold > hi ? null : (
            <line
              key={`t${s.key}`}
              className={styles.threshold}
              data-tone={s.tone}
              x1={PAD_L} x2={PAD_L + iw}
              y1={py(s.threshold)} y2={py(s.threshold)}
              style={{ stroke: colourOf(s, i) }}
            />
          ),
        )}

        {series.map((s, i) =>
          path(s).map((d) => (
            <path
              key={`${s.key}-${d.length}-${d.slice(0, 12)}`}
              className={styles.line}
              data-tone={s.tone}
              d={d}
              style={{ stroke: colourOf(s, i) }}
            />
          )),
        )}

        {/* Tick labels sit just above their gridline, upright and left. The
            clamp keeps the topmost one inside the box instead of half-cut. */}
        {ys.map((v, i) => (
          <text
            key={`yl${v}`}
            className={styles.yTick}
            x={2}
            y={Math.max(9, Math.min(height - 1, py(v) - 3))}
          >
            {Number.isInteger(v) ? v : v.toFixed(1)}
            {i === ys.length - 1 && unit !== undefined && (
              <tspan className={styles.yUnit}>{unit}</tspan>
            )}
          </text>
        ))}

        {now !== undefined && now >= from && now <= to && (
          <line
            className={styles.playhead}
            x1={PAD_L + ((now - from) / (to - from || 1)) * iw}
            x2={PAD_L + ((now - from) / (to - from || 1)) * iw}
            y1={0} y2={height}
          />
        )}

        {cursor !== null && cursor >= 0 && cursor < times.length && (
          <>
            {!driven && (
              <line
                className={styles.crosshair}
                x1={cursorX} x2={cursorX} y1={0} y2={height}
              />
            )}
            {series.map((s, i) => {
              const v = s.values[cursor];
              if (v == null || Number.isNaN(v)) return null;
              return (
                <circle
                  key={`c${s.key}`}
                  className={styles.marker}
                  data-tone={s.tone}
                  cx={px(cursor)} cy={py(v)} r={2}
                  style={{ fill: colourOf(s, i) }}
                />
              );
            })}
          </>
        )}
      </svg>

      {legend && series.length > 0 && (
        <div className={styles.legend}>
          {series.map((s, i) => (
            <span key={s.key} className={styles.legendItem} data-tone={s.tone}>
              <span className={styles.swatch} style={{ background: colourOf(s, i) }} />
              {s.label}
            </span>
          ))}
        </div>
      )}

      {readout && cursor !== null && cursor >= 0 && cursor < times.length && (
        <div
          className={styles.readout}
          data-side={cursor / Math.max(1, times.length - 1) > 0.55 ? 'left' : 'right'}
        >
          <div className={styles.readoutTime}>{axisClock(times[cursor]!)}</div>
          {series.map((s) => {
            const v = s.values[cursor];
            const known = v != null && !Number.isNaN(v);
            return (
              <div key={s.key} className={styles.readoutRow}>
                <span className={styles.readoutKey}>{s.label}</span>
                <span className={styles.readoutVal} data-known={known}>
                  {known ? v.toFixed(s.digits ?? 1) : '—'}
                  {known && (s.unit ?? unit) !== undefined && (
                    <span className={styles.readoutUnit}>{s.unit ?? unit}</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
